import React, { PureComponent, ChangeEvent, MouseEvent } from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import styled from 'styled-components';
import orderBy from 'lodash/orderBy';
import { Typography, Button, Label, Grid, Colors } from '@inplayer-org/inplayer-ui';
import { MdInfoOutline } from 'react-icons/md';

// utils
import media from 'utils/mediaQueries';

// actions
import {
  createApplication as createApplicationAction,
  loadApplications as loadApplicationsAction,
  updateApplication as updateApplicationAction,
  loadApplicationSecret as loadApplicationSecretAction,
  loadDomains as loadDomainsAction,
  UpdateApplication,
} from 'ducks/merchantDucks/oauth/actions';
import { openModal as openModalAction, closeModal as closeModalAction } from 'ducks/merchantDucks/modal/actions';

// common components
import SubheaderWithBorder from 'components/SubheaderWithBorder';
import MainHolder from 'components/MainHolder';
import ContentHolder from 'components/ContentHolder';
import StyledContainer from 'components/StyledContainer';
import BoxContainer, { BoxAdderParams } from 'components/BoxContainer';
import BoxItemDefaultGrid from 'components/BoxItemDefaultGrid';
import SecretKeyModal from 'pages/MerchantPages/Settings/Auth/Oauth/components/modals/SecretKeyModal';
import ConfirmModal from 'components/Modals/ConfirmModal';
import CommonIcon from 'components/CommonIcon';

// page components
import OauthItem from 'pages/MerchantPages/Settings/Auth/Oauth/components/OauthItem';
import EnableSSO from 'pages/MerchantPages/Settings/Auth/Oauth/components/modals/EnableSSO';
import AppBoxAdder from 'pages/MerchantPages/Settings/Auth/Oauth/components/AppBoxAdder';

// types
import { Item } from 'components/BoxItem';
import OAuthState from 'ducks/merchantDucks/oauth/types';
import RootState from 'ducks/RootState';
import { SUB_DOMAIN_URL } from 'constants/index';
import { ModalNames } from 'ducks/merchantDucks/modal/types';

// constants
import Rules from 'restrictions/roles/constants/rules';

// components
import Restricted from 'restrictions/roles/components/Restricted';
import TourGuideWrapper from 'components/TourGuide';
import { BlueTypography } from 'components/SharedStyledElements';

const { Container, Cell } = Grid;

const OauthItemGrid = styled(BoxItemDefaultGrid)`
  grid-template-columns: repeat(3, minmax(31%, 1fr));

  ${media.customMaxWidth(1200)} {
    grid-template-columns: repeat(2, minmax(48%, 1fr));
  }
`;

const DomainGate = styled(ContentHolder)`
  display: grid;
  grid-template-columns: 9rem 1fr;
  grid-gap: 1rem;
  border-top: 1px solid ${Colors.gray};
  padding-top: 2rem;
`;

const StyledSubdomain = styled.span`
  color: ${({ theme }) => theme.palette.primary.main};
`;

export const Note = styled(Typography)`
  margin-top: 2rem;
  border: 1px solid ${Colors.gray};
  border-radius: 3px;
  padding: 1rem;
`;

export const InfoIcon = styled(MdInfoOutline)`
  margin-right: 0.5rem;
`;

export const ItalicSpan = styled.span`
  font-style: italic;
`;

interface StateProps {
  oauth: OAuthState;
  roleRestrictions: Rules[];
}

interface DispatchProps {
  createApplication: (name: string) => any;
  loadApplications: () => any;
  updateApplication: ({ id, sso, name }: UpdateApplication) => any;
  loadApplicationSecret: (id: string | number) => any;
  loadDomains: () => any;
  openModal: (modalName: ModalNames) => void;
  closeModal: () => void;
}

interface OwnProps {}

type Props = StateProps & DispatchProps & OwnProps;

interface ToggleId {
  [key: string]: boolean;
}

interface State {
  name: string;
  toggleId: ToggleId;
  edit: boolean;
  appId: string;
}

export class Oauth extends PureComponent<Props, State> {
  state: State = {
    name: '',
    toggleId: {},
    edit: false,
    appId: '',
  };

  componentDidMount() {
    const { loadApplications, loadDomains } = this.props;
    loadApplications();
    loadDomains();
  }

  componentDidUpdate(prevProps: Props) {
    const {
      oauth: { collection },
    } = this.props;

    const {
      oauth: { collection: prevCollection },
    } = prevProps;

    const toggleId: ToggleId = collection.reduce((result, item) => ({ ...result, [item.id]: item.sso }), {});

    if (prevCollection !== collection) {
      this.setState({ toggleId });
    }
  }

  handleCheck = ({ checked, id }: { checked: boolean; id: string }) => {
    const { updateApplication, openModal } = this.props;

    if (checked) {
      openModal(ModalNames.ConfirmEnableSSO);
      this.setState({ appId: id });
    } else {
      updateApplication({ id, sso: 0 });
    }
  };

  handleEnable = () => {
    const { appId } = this.state;
    const { updateApplication } = this.props;

    updateApplication({ id: appId, sso: 1 });
  };

  createNew = (name: string) => {
    const { createApplication } = this.props;
    createApplication(name);
  };

  generateNew = (id: string) => {
    const { openModal } = this.props;
    this.setState({ appId: id });
    openModal(ModalNames.GenerateSecretKey);
  };

  handleGenerateKey = () => {
    const { appId } = this.state;
    const { loadApplicationSecret, openModal } = this.props;

    loadApplicationSecret(appId);
    openModal(ModalNames.SecretKey);
  };

  hideModals = () => {
    const { closeModal } = this.props;
    closeModal();
  };

  nameChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    this.setState({ name: value });
  };

  titleChange = async (app: Item) => {
    const { name } = this.state;
    const { updateApplication } = this.props;

    if (name) {
      await updateApplication({ id: app.id, name });
    }
  };

  handleModal = (e: MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    const { openModal } = this.props;
    openModal(ModalNames.EnableSSO);
  };

  appItemAdderRender = ({ itemTitle }: BoxAdderParams) => (
    <AppBoxAdder itemTitle={itemTitle} handleAddNew={this.createNew} />
  );

  oauthAppItemBoxRender = (isReadOnly: boolean) => (item: Item) => {
    const {
      oauth: { subdomain },
    } = this.props;

    const { id } = item;
    const { toggleId } = this.state;

    return (
      <OauthItem
        boxItem={item}
        filled={!!subdomain}
        checked={toggleId[id.toString()]}
        onTitleChange={this.titleChange}
        onNameChange={this.nameChange}
        onClickGenerateNew={this.generateNew}
        toggleSSO={this.handleCheck}
        isReadOnly={isReadOnly}
      />
    );
  };

  hasRule = (rule: Rules) => {
    const { roleRestrictions } = this.props;
    return roleRestrictions.includes(rule);
  };

  render() {
    const {
      oauth: {
        collection,
        secret,
        subdomain,
        domainInputs: { origins, inputs },
      },
    } = this.props;

    const filled = !!subdomain;

    const orderedCollection = orderBy(collection, ['created_at'], ['desc']);
    const oauthAppsCollection = orderedCollection.map((item) => ({ item }));

    const isReadOnly = this.hasRule(Rules.READONLY_OAUTH_AND_SSO_SETUP);

    return (
      <>
        <TourGuideWrapper withSideNav />
        <MainHolder>
          <SubheaderWithBorder>
            <ContentHolder>
              <BlueTypography variant="h1">OAuth & SSO</BlueTypography>
            </ContentHolder>
          </SubheaderWithBorder>
          <ContentHolder>
            <StyledContainer columns={1} rowGap="2rem">
              <Typography variant="p">
                With OAuth, you can split your customers into groups (called apps), based upon on which asset they have
                registered their account. You can attach an OAuth app to an asset in the asset embed code screen.
              </Typography>
              <Typography variant="p">
                With SSO you can sync customer logins across multiple domains. When a customer logs into their account
                on one domain and then goes to another included domain, they will already be logged in.
              </Typography>
            </StyledContainer>
          </ContentHolder>
          {filled ? (
            <DomainGate>
              <Label>Subdomain:</Label>
              <StyledSubdomain>{`https://${subdomain}.${SUB_DOMAIN_URL}`}</StyledSubdomain>
              <Label>{origins.length} domains added</Label>
              <CommonIcon tag="icon_edit" hoverStatus="edit" name="edit" onClick={this.handleModal} />
            </DomainGate>
          ) : (
            <Restricted isRestricted={isReadOnly}>
              <Container as={ContentHolder} columns="10rem 1fr">
                <Cell>
                  <Button tag="button_enable_sso" fullWidth type="button" onClick={this.handleModal}>
                    SSO Enable...
                  </Button>
                </Cell>
              </Container>
            </Restricted>
          )}
          <BoxContainer
            itemTitle="app"
            items={oauthAppsCollection}
            handleAddNew={this.createNew}
            boxItemRender={this.oauthAppItemBoxRender(isReadOnly)}
            renderCustomBoxAdder={this.appItemAdderRender}
            customItemGrid={OauthItemGrid}
            isReadOnly={isReadOnly}
          />
        </MainHolder>
        <SecretKeyModal secret={secret} />
        <EnableSSO domains={inputs} />
        <ConfirmModal
          modalName={ModalNames.ConfirmEnableSSO}
          handleConfirm={this.handleEnable}
          message="Enable SSO for this OAuth app?"
          buttonText="enable"
          description="Please make sure you have the OAuth application ID and your merchant sub-domain included in the embed code
          of assets to which end users should be simultaneously logged in."
          confirmButtonTag="button_save"
        />
        <ConfirmModal
          modalName={ModalNames.GenerateSecretKey}
          handleConfirm={this.handleGenerateKey}
          message="Are you sure you want to generate a new app secret key?"
          buttonText="generate"
          shouldCloseModal={false}
          description="Be aware that the previous app secret key will immediately become invalid."
          title="Generate a new app secret key"
        />
      </>
    );
  }
}

export const mapStateToProps: MapStateToProps<StateProps, OwnProps, RootState> = (state) => ({
  oauth: state.oauth,
  roleRestrictions: state.auth.user.roleRestrictions,
});

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = {
  createApplication: createApplicationAction,
  loadApplications: loadApplicationsAction,
  updateApplication: updateApplicationAction,
  loadApplicationSecret: loadApplicationSecretAction,
  loadDomains: loadDomainsAction,
  openModal: openModalAction,
  closeModal: closeModalAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(Oauth);
